// ─────────────────────────────────────────────────────────────────────────────
// LocationsTabComponent
// ─────────────────────────────────────────────────────────────────────────────
// WB-7570: Knowledge Base "Ignore locations for shipping" list. Every location
// is a valid shipping candidate by default; a row here removes one from that
// pool (e.g. a main/billing-only location that has a shipping-eligible twin --
// Equilibra E-/D-prefix codes, Euroclone BIO0000... locations). Flat,
// unpaginated list -- expected to stay small. Checkbox selection + bulk delete
// mirror the list-ignore-codes pattern (SelectionController + bulk-actions-bar).
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { ILocationShippingExclusion } from '../../models/location-shipping-exclusion.models';
import {
  selectLocationShippingExclusionItems,
  selectLocationShippingExclusionLoading,
} from '../../store/location-shipping-exclusions.selectors';
import {
  BulkDeleteLocationShippingExclusions,
  DeleteLocationShippingExclusion,
  LoadLocationShippingExclusions,
} from '../../store/location-shipping-exclusions.actions';
import { ShippingExclusionModalComponent } from '../shipping-exclusion-modal/shipping-exclusion-modal.component';

@Component({
  selector: 'app-locations-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './locations-tab.component.html',
  styleUrls: ['./locations-tab.component.scss'],
})
export class LocationsTabComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  exclusions$: Observable<ILocationShippingExclusion[]> = this.store.select(selectLocationShippingExclusionItems);
  loading$: Observable<boolean> = this.store.select(selectLocationShippingExclusionLoading);

  // ── Local state ───────────────────────────────────────────────────────────

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ILocationShippingExclusion>((item) => item.location_id);
  sortedExclusions: ILocationShippingExclusion[] = [];

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.exclusions$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.sortedExclusions = items;
      this.selection.setRows(items);
    });

    this.store.dispatch(LoadLocationShippingExclusions());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Dialogs ───────────────────────────────────────────────────────────────

  openAddModal(): void {
    this.dialog.open(ShippingExclusionModalComponent, {
      disableClose: true,
      // Matches SharedDialogOptions size:'large' set in the modal itself.
      // A MatDialog `width` wider than the shared-dialog's own `size` preset
      // does NOT actually widen the visible card -- `.mat-dialog-container`
      // is a flex item under CDK's overlay pane with `align-items: center`,
      // so it shrink-wraps to its content (the `size` preset) regardless of
      // a larger pane width, leaving an empty gutter. Every other KB add
      // dialog in this app keeps these two in lockstep for the same reason.
      width: '900px',
    });
  }

  remove(item: ILocationShippingExclusion): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.store.dispatch(DeleteLocationShippingExclusion({ locationId: item.location_id }));
        }
      });
  }

  // ── Bulk delete ───────────────────────────────────────────────────────────

  bulkDelete(): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.store.dispatch(BulkDeleteLocationShippingExclusions({ locationIds: this.selection.ids }));
          this.selection.clear();
        }
      });
  }
}
